<template>
    <div
        ref="wrapper"
        class="snapgrid"
        :class="theme.wrapper"
        :data-name="props.name"
    >
        <div :class="theme.wrapper">
            <app-header v-if="config.show_header" :data="data" />

            <div
                :class="{
                    [theme.grid]: true,
                    [theme.fixedHeight]: config.height !== 'auto',
                }"
            >
                <table cellpadding="0" cellspacing="0">
                    <thead v-if="config.show_columns">
                        <tr>
                            <th
                                v-if="config.checkboxes_enabled"
                                class="snapgrid-col"
                                :class="theme.checkbox"
                            >
                                <input
                                    v-if="config.allow_multiple_checked_rows"
                                    ref="checkAll"
                                    type="checkbox"
                                    @change="checkAll"
                                />
                            </th>

                            <app-column
                                v-for="(col, index) in data.cols"
                                :key="`col-${index}`"
                                :data="data"
                                :col="col"
                                @col-selected="sort"
                            />

                            <th ref="headerScrollbarSpacer"></th>
                        </tr>
                    </thead>

                    <tbody ref="tbody" :style="{ height: tbodyHeight }">
                        <app-row
                            v-for="(row, index) in rows"
                            :key="`row-${row.id}`"
                            :ref="`row-${row.id}`"
                            :data="data"
                            :row="row"
                            :index="index"
                            :is-placeholder="false"
                            :cell-styles="cellStyles"
                        />

                        <app-row
                            v-for="(row, index) in placeholderRows"
                            :key="`placeholder-row-${index}`"
                            :data="data"
                            :row="{}"
                            :is-placeholder="true"
                            :cell-styles="[]"
                        />
                    </tbody>
                </table>

                <div v-show="isEmpty && false" :class="theme.empty">
                    <span :class="theme.message">&nbsp;</span>
                </div>
            </div>

            <app-footer v-if="config.show_footer" :data="data" />
        </div>

        <div v-show="false" style="margin: 20px; text-align: center">
            <div
                v-show="false"
                style="margin: 20px; text-align: left"
                v-text="debug"
            ></div>
        </div>

        <app-context-menu
            ref="contextMenu"
            :data="data"
            :options="config.context_menu_options"
            @hidden="contextMenuHidden"
            @option-selected="contextMenuOptionSelected"
        />
    </div>
</template>

<script>
import Vue from 'vue';
import SnapGridInterface from './classes/SnapGridInterface';
import Header from './components/Header.vue';
import Column from './components/Column.vue';
import Row from './components/Row.vue';
import Footer from './components/Footer.vue';
import ContextMenu from './components/ContextMenu.vue';
import Common from './components/mixins/Common.vue';
import ReloadsGrid from './components/mixins/ReloadsGrid.vue';
import UsesFilters from './components/mixins/UsesFilters.vue';
import ProvidesStyles from './components/mixins/ProvidesStyles.vue';
import UsesBindRelations from './components/mixins/UsesBindRelations.vue';
import HasTableHeaderScrollbarSpacer from 'larasnap/assets/vue/support/table_scrollbar_spacer/mixins/HasTableHeaderScrollbarSpacer.vue';
import { getSnapGridClass, getPanelTemplate } from 'larasnap';
import {
    isNumeric,
    parseBind,
    remove,
    getUrlParam,
    snapgrid,
    Confirm,
} from 'larasnap/support';
import kebabCase from 'lodash-es/kebabCase';
import isObject from 'lodash-es/isObject';
import isMatch from 'lodash-es/isMatch';

export default {
    components: {
        'app-header': Header,
        'app-column': Column,
        'app-row': Row,
        'app-footer': Footer,
        'app-context-menu': ContextMenu,
    },
    mixins: [
        Common,
        ReloadsGrid,
        UsesFilters,
        ProvidesStyles,
        UsesBindRelations,
        HasTableHeaderScrollbarSpacer,
    ],
    props: {
        gridData: Object,
    },
    data() {
        const data = this.gridData;

        if (typeof data.config.bindTo === 'string') {
            data.config.bindTo = [data.config.bindTo];
        } else if (!Array.isArray(typeof data.config.bindTo)) {
            data.config.bindTo = [];
        }

        data.config.bindTo = data.config.bindTo.map(parseBind);

        data.props.binds = [];
        data.shiftKey = false;
        data.handlers = [];
        data.tbodyHeight = 500;
        data.rowCount = null;
        data.headerHeight = 0;
        data.footerHeight = 0;
        data.tableHeaderHeight = 0;

        if (data.config.context_menu_delete_enabled) {
            data.config.context_menu_options.push(
                data.config.context_menu_delete_option,
            );
        }

        if (data.config.context_menu_options.length === 0) {
            data.config.context_menu_enabled = false;
        }

        data.bus = new Vue(); // Must handle shared event bus this way because the typical method of importing from a file
        // in every component will cause the event bus to be shared by every instance of SnapGrid

        data.contextMenuVisible = false;
        data.filterMenuVisible = false;

        data.allCols = data.cols;
        data.cols = data.cols.filter(x => !x.printOnly);

        return {
            headerScrollbarSpacerConfig: {
                refName: 'headerScrollbarSpacer',
                rowsName: 'rows',
            },
            data,
        };
    },
    computed: {
        cellStyles() {
            return this.data.cols.map(col => this.getStyle(col, 'cell'));
        },
        placeholderRows() {
            // Placeholder rows make it so the height of the table is always the same regardless of which page the user is on.
            // Usually on the last page there will be less rows than on all the previous pages unless the total number of rows
            // is divisible by the current page size.

            const totalPages = this.totalPages;

            if (
                totalPages > 1 &&
                this.rows.length < this.props.page_size_actual
            ) {
                return new Array(
                    this.props.page_size_actual - this.rows.length,
                );
            } else {
                return [];
            }
        },
        debug() {
            return this.getPropDebug(this.prop);
        },
        tbodyHeight() {
            if (this.config.height === 'fluid') {
                return `${this.data.tbodyHeight}px`;
            } else {
                return 'auto';
            }
        },
    },
    watch: {
        'data.tbodyHeight': function () {
            this.updateAutomaticPageSize();
        },
    },
    mounted() {
        //console.log('----------SnapGrid mounted.-----------', this.data);

        //this.setProps("page_num", 2);
        //this.reload();

        //this.toggleFilterMenu();

        //const updateHeight = () => this.data.headerHeight = $(this.$el).outerHeight();
        //updateHeight();

        this.data.tableHeaderHeight = $(this.$el)
            .find('table thead')
            .outerHeight();

        // [Dev Utility] Auto row click
        if (getUrlParam('sg')) {
            setTimeout(() => {
                const defaultName = snapgrid().getProps('name');
                getUrlParam('sg')
                    .split(',')
                    .map(x => (x.includes(':') ? x : `${defaultName}:${x}`))
                    .map(x => ({
                        grid: x.split(':')[0],
                        row: x.split(':')[1],
                    }))
                    .filter(
                        ({ grid }) =>
                            kebabCase(grid) === kebabCase(this.props.name),
                    )
                    .map(({ row }) =>
                        row.startsWith('id-')
                            ? row.replace('id-', '')
                            : `#${row}`,
                    )
                    .forEach(this.clickRow);
            }, 100);
        }

        this.bus.$on('context-menu-show', () => {
            this.$refs.contextMenu.show();
            this.data.contextMenuVisible = true;
        });

        this.bus.$on('context-menu-hide', () => {
            this.$refs.contextMenu.hide();
        });

        this.bus.$on('paze-size-changed', () => {
            this.reload().then(this.resetScrollPosition);
        });

        this.bus.$on('filter-changed', () => {
            this.setPage(1);
        });

        const name = this.getBindRelation('Panel');

        if (this.config.show_add_button && name) {
            const template = getPanelTemplate(name);
            if (template) {
                const label = template.label;

                if (!this.config.add_button_label) {
                    this.config.add_button_label = `New ${label}`;
                }

                this.data.config.buttons.push({
                    type: 'addButton',
                    icon: 'icon-add',
                    label: this.config.add_button_label,
                    load_panel_props: this.config.add_button_load_panel_props,
                    dusk: 'snapgrid-add-button',
                });
            }
        }

        if (isNumeric(this.config.height)) {
            this.setHeight(this.config.height);
        }

        if (this.config.checkboxes_enabled) {
            document.addEventListener('keydown', event => {
                if (event.keyCode === 16) {
                    this.data.shiftKey = true;
                }
            });
            document.addEventListener('keyup', event => {
                if (event.keyCode === 16) {
                    this.data.shiftKey = false;
                }
            });
        }

        this.bus.$on('set-page', this.setPage);

        const cls = getSnapGridClass(this.props.name);
        if (cls) {
            this.data.interface = new cls(this);
        } else {
            this.data.interface = new SnapGridInterface(this);
        }
    },
    methods: {
        updateAutomaticPageSize() {
            const totalHeight = this.data.tbodyHeight;
            const rowHeight = $(this.$refs.tbody).children(':first').height();
            const oldAutomaticPageSize = this.props.automatic_page_size;
            if (!rowHeight) return;

            this.props.automatic_page_size = Math.floor(
                totalHeight / rowHeight,
            );

            if (this.props.page_size === 'auto') {
                this.props.page_size_actual = this.props.automatic_page_size;

                if (oldAutomaticPageSize !== this.props.automatic_page_size) {
                    this.reload({ hidden: true });
                }
            }
        },
        contextMenuHidden() {
            this.data.contextMenuVisible = false;
            this.bus.$emit('context-menu-hidden');
        },
        contextMenuOptionSelected(option) {
            let continueHandlers =
                this.data.interface.contextMenuOptionSelected(option);
            if (continueHandlers === false) {
                return;
            }

            continueHandlers = this.runHandler('menu-option-selected', option);
            if (continueHandlers === false) {
                return;
            }

            if (option === 'delete') {
                this.deleteRows();
            }
        },
        deleteRows() {
            const ids = this.getCheckedRowIds();
            let description;
            if (this.config.label) {
                description =
                    ids.length > 1
                        ? `these ${ids.length} ${this.config.label_plural}`
                        : `this ${this.config.label}`;
            } else {
                description =
                    ids.length > 1 ? `these ${ids.length} rows` : 'this row';
            }

            Confirm.delete({
                message: `Are you sure you want to delete ${description.toLowerCase()}?`,
            }).then(({ isConfirmed }) => {
                if (isConfirmed) {
                    this.$http('deleteRows', ids)
                        .send()
                        .then(() => this.reload());
                }
            });
        },
        getCheckedRows() {
            return this.data.rows.filter(x => x.checked);
        },
        checkAll() {
            const isChecked = $(this.$refs.checkAll).is(':checked');
            this.rows.forEach(row => {
                row.checked = isChecked;
            });
            $(this.$refs.checkAll).prop('checked', isChecked);
        },
        clearCheckedRows() {
            this.data.rows.forEach(x => (x.checked = false));
            $(this.$refs.checkAll).prop('checked', false);
        },
        bind(type, name, data) {
            this.props.binds.push({ type, name, data });
        },
        unbind(type, name, data) {
            remove(this.props.binds, { type, name, data });
        },
        setHeight(height) {
            this.data.tbodyHeight =
                height -
                this.data.headerHeight -
                this.data.footerHeight -
                this.data.tableHeaderHeight;

            this.$nextTick(() => {
                if (this.config.show_columns) {
                    this.computeHeaderMargin();
                }
            });
        },
        clickRow(params) {
            if (typeof params === 'string' && params.startsWith('#')) {
                this.clickRowAtPosition(Number(params.substr(1)));
                return;
            }

            const row = this.getRow(params);
            if (row) {
                row.click();
            }
        },
        clickRowAtPosition(params) {
            const row = this.getRowByPosition(params);
            if (row) {
                row.click();
            }
        },
        getRow(params) {
            if (!isObject(params)) {
                const row = this.$refs[`row-${params}`];
                if (row) {
                    return row[0];
                } else {
                    this.handleError(`Cannot find row with id: ${params}`);
                }
            } else {
                const row = this.rows.find(x => isMatch(x.fields, params));
                if (row) {
                    return this.$refs[`row-${row.id}`][0];
                } else {
                    this.handleError(
                        `Cannot find row with params: ${JSON.stringify(
                            params,
                        )}`,
                    );
                }
            }
        },
        getRowByPosition(param) {
            if (typeof param === 'number') {
                const id = this.rows[param - 1].id;
                if (id) {
                    return this.getRow(id);
                } else {
                    this.handleError(`There is no row at position: ${param}`);
                }
            }
        },
        getElement(name) {
            return $(this.$refs[name]);
        },

        getPropDebug(props) {
            const html = [];

            for (const i in props) {
                let val;

                if (i === 'contains') {
                    continue;
                } else if (
                    Array.isArray(props[i]) &&
                    props[i].length > 0 &&
                    !isObject(props[i][0])
                ) {
                    val = `[ ${props[i].join(', ')} ]`;
                } else if (isObject(props[i])) {
                    val = `<div style="margin-left: 25px">${this.getPropDebug(
                        props[i],
                    )}</div>`;
                } else {
                    val = props[i];
                }

                html.push(`<div><b>${i}</b>: ${val}</div>`);
            }

            return html.join();
        },
        sort(colIndex) {
            if (this.props.sort_col === colIndex) {
                this.setProps(
                    'sort_dir',
                    this.props.sort_dir === 'asc' ? 'desc' : 'asc',
                );
            } else {
                this.setProps('sort_col', colIndex);
            }

            this.setPage(1);
        },
        setPage(num) {
            this.setProps('page_num', num);
            this.reload().then(this.resetScrollPosition);
        },
        getCheckedRowIds() {
            return this.getCheckedRows().map(x => x.id);
        },
        resetScrollPosition() {
            $(this.$refs.tbody).scrollTop(0);
        },
    },
};
</script>

<style lang="scss">
.scrollbar-measure {
    width: 100px;
    height: 100px;
    overflow: scroll;
    position: absolute;
    top: -9999px;
}
</style>

<style lang="scss" module></style>
<style
    lang="scss"
    module="theme_default"
    src="./css/themes/default/snapgrid.scss"
></style>
<style
    lang="scss"
    module="theme_simple"
    src="./css/themes/simple/snapgrid.scss"
></style>
